import React, { Fragment, useState, useEffect, useCallback, useRef } from 'react';
import { PlaidEmbeddedLink } from 'react-plaid-link';
import { useSelector, useStore, useDispatch } from 'react-redux';

import Wallet, { exchangePlaidToken } from 'businessLogic/Wallet';
import PlaidErrorScreen from 'components/Core/Payment/Growth/PlaidExperiment/PlaidErrorScreen';
import Spinner from 'components/Shared/Spinner/Spinner';
import SegmentIO from 'reporting/SegmentIO';
import { SplunkReporter } from 'reporting/splunk/SplunkReporter';
import { paymentActions, submitPayment } from 'store/payment/slice';
import { RootState, AppDispatch } from 'store/store';
import { walletActions, REMOTE_BUTTON_ACCESS_FUNCTIONALITY } from 'store/wallet/slice';
import { breakpoints } from 'styles/cp';

const splunkReporter = SplunkReporter.getInstance();
const logger = 'components/Core/Payment/Growth/PlaidExperiment/PlaidBankTabs';
const DEFAULT_PLAID_LINK_RETRIES = 2;
const PLAID_EMBEDDED_LINK_LOAD_TIMEOUT_MS = 10000;

export type PlaidBankTabsProps = {
  bindFormSubmission: (...args: any[]) => any;
  isActive: boolean;
  onSwitchToManual: () => void;
  onPlaidUnavailable: () => void;
  domainId?: string;
};

type PlaidLinkState = {
  linkToken: string | null;
  isLoading: boolean;
  error: Error | null;
};

export type PlaidBankDetails = {
  token: string;
  name: string;
  accountType: string | null;
  phone: string;
  accountNumber: string;
};

const PlaidBankTabs: React.FC<PlaidBankTabsProps> = (props) => {
  const [plaidState, setPlaidState] = useState<PlaidLinkState>({
    linkToken: null,
    isLoading: false,
    error: null,
  });

  const walletRef = useRef(new Wallet());
  const plaidLinkRetryCountRef = useRef(0);
  const plaidLoadedRef = useRef(false);
  const onPlaidUnavailableRef = useRef(props.onPlaidUnavailable);
  onPlaidUnavailableRef.current = props.onPlaidUnavailable;
  const [plaidBankDetails, setPlaidBankDetails] = useState<PlaidBankDetails | null>(null);
  const [exchangeError, setExchangeError] = useState<Error | null>(null);
  const store = useStore<RootState>();
  const realmId = useSelector((state: RootState) => state.auth?.realmId);
  const recipientEmail = useSelector((state: RootState) => state.auth?.recipientEmail);
  const maxPlaidLinkRetries = useSelector(
    (state: RootState) => state.config?.plaid?.retries ?? DEFAULT_PLAID_LINK_RETRIES
  );
  const domainId = props.domainId;
  const domainIdRef = useRef(domainId);
  domainIdRef.current = domainId;
  const dispatch = useDispatch<AppDispatch>();

  const prevIsActive = useRef(props.isActive);

  const fetchLinkToken = useCallback(async () => {
    splunkReporter.contextual({
      logInfo: { logLevel: 'info', logger },
      event: 'pay',
      action: 'render',
      activityInfo: {
        activityObject: 'PlaidLinkToken',
        plaidAction: 'fetch_link_token_started',
        realmId,
        domainId,
      },
    });

    setPlaidState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const currentState = store.getState();
      const { linkToken, plaidReferenceId } = await walletRef.current.fetchPlaidLinkToken(
        currentState
      );
      setPlaidState({
        linkToken,
        isLoading: false,
        error: null,
      });

      splunkReporter.contextual({
        logInfo: { logLevel: 'info', logger },
        event: 'pay',
        action: 'render',
        activityInfo: {
          activityObject: 'PlaidLinkToken',
          plaidAction: 'fetch_link_token_success',
          realmId,
          domainId,
          plaidReferenceId,
        },
      });
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Failed to fetch Plaid link token');
      setPlaidState({
        linkToken: null,
        isLoading: false,
        error: err,
      });

      splunkReporter.contextual({
        logInfo: { logLevel: 'error', logger },
        event: 'pay',
        action: 'render',
        activityInfo: {
          activityObject: 'PlaidLinkToken',
          plaidAction: 'fetch_link_token_error',
          realmId,
          domainId,
        },
        error: {
          message: err.message,
        },
      });

      props.onPlaidUnavailable();
    }
  }, [store, realmId, props.onPlaidUnavailable]);

  // Fetch link token when active and no token exists
  useEffect(() => {
    if (props.isActive && !plaidState.linkToken && !plaidState.isLoading && !plaidState.error) {
      fetchLinkToken();
    }
  }, [
    props.isActive,
    plaidState.linkToken,
    plaidState.isLoading,
    plaidState.error,
    fetchLinkToken,
  ]);

  // Reset errors when becoming active again after being inactive
  useEffect(() => {
    if (props.isActive && !prevIsActive.current) {
      if (plaidState.error || exchangeError) {
        plaidLinkRetryCountRef.current = 0;
        plaidLoadedRef.current = false;
        setPlaidState({ linkToken: null, isLoading: false, error: null });
        setExchangeError(null);
        setPlaidBankDetails(null);
      }
    }
    prevIsActive.current = props.isActive;
  }, [props.isActive, plaidState.error, exchangeError]);

  // Bind pay button to no op when there's an error
  useEffect(() => {
    if ((plaidState.error || exchangeError) && props.isActive && props.bindFormSubmission) {
      props.bindFormSubmission({
        fn: () => {},
        functionality: REMOTE_BUTTON_ACCESS_FUNCTIONALITY.BANK_CREATE_PAYMENT,
      });
      splunkReporter.contextual({
        logInfo: { logLevel: 'info', logger },
        event: 'view',
        action: 'click',
        activityInfo: {
          activityObject: 'PlaidPayButton',
          plaidAction: 'switch_to_manual_tab_on_error',
          domainId,
        },
      });
    }
  }, [plaidState.error, exchangeError, props.isActive, props.bindFormSubmission]);

  // Track view whenever the Plaid tab is shown (initial load or when switching from manual to plaid)
  useEffect(() => {
    if (props.isActive) {
      splunkReporter.contextual({
        logInfo: { logLevel: 'info', logger },
        event: 'view',
        action: 'render',
        activityInfo: {
          activityObject: 'PlaidBankTabs',
          initialTab: 'instant',
          domainId,
        },
      });
      SegmentIO.plaidInstantTabsViewed(domainId, recipientEmail);
    }
  }, [props.isActive, domainId, recipientEmail]);

  // Bind the Plaid payment submission to the PAY button when bank details are available
  useEffect(() => {
    if (plaidBankDetails && props.isActive && props.bindFormSubmission) {
      const plaidSubmitPayment = async () => {
        splunkReporter.contextual({
          logInfo: { logLevel: 'info', logger },
          event: 'pay',
          action: 'click',
          activityInfo: {
            activityObject: 'PlaidPayment',
            plaidAction: 'submit_payment',
            accountType: plaidBankDetails?.accountType,
            domainId,
          },
        });

        SegmentIO.plaidPaymentSubmitted(domainId, recipientEmail);
        dispatch(paymentActions.createPayment(undefined));
        dispatch(walletActions.resetSavedMerchantWalletConfirmationFlags());

        try {
          await dispatch(submitPayment({ bankDetails: plaidBankDetails }));
          const payment = store?.getState()?.payment;
          const paymentStatus = payment?.paymentStatus;
          const lastSuccessfulPaymentResponse = payment?.lastSuccessfulPaymentResponse;
          const paymentResponsePaymentStatus =
            typeof lastSuccessfulPaymentResponse === 'object'
              ? lastSuccessfulPaymentResponse?.paymentStatus
              : undefined;
          const amountPaid =
            typeof lastSuccessfulPaymentResponse === 'object'
              ? lastSuccessfulPaymentResponse?.amountPaid
              : undefined;
          const paymentErrorMsg = payment?.paymentError?.message;
          const paymentFailureCount = payment?.paymentFailureCount;

          splunkReporter.contextual({
            logInfo: { logLevel: 'info', logger },
            event: 'pay',
            action: 'submit',
            activityInfo: {
              activityObject: 'PlaidPayment',
              plaidAction: 'submit_payment_result',
              paymentExperience: 'plaid',
              paymentStatus,
              accountType: plaidBankDetails?.accountType,
              paymentResponsePaymentStatus,
              amountPaid,
              paymentErrorMsg,
              paymentFailureCount,
              domainId,
            },
          });
        } catch (error) {
          const errorMessage = error instanceof Error ? error.message : String(error);

          splunkReporter.contextual({
            logInfo: { logLevel: 'error', logger },
            event: 'pay',
            action: 'submit',
            activityInfo: {
              activityObject: 'PlaidPayment',
              plaidAction: 'submit_payment_result',
              paymentExperience: 'plaid',
              paymentStatus: 'error',
              accountType: plaidBankDetails?.accountType,
              domainId,
            },
            error: { message: errorMessage },
          });
        }
      };

      props.bindFormSubmission({
        fn: plaidSubmitPayment,
        functionality: REMOTE_BUTTON_ACCESS_FUNCTIONALITY.BANK_CREATE_PAYMENT,
      });
    }
  }, [
    plaidBankDetails,
    props.isActive,
    dispatch,
    props.bindFormSubmission,
    domainId,
    recipientEmail,
  ]);

  // Bind no-op to pay button while Plaid flow is in progress
  useEffect(() => {
    if (
      props.isActive &&
      !plaidBankDetails &&
      !plaidState.error &&
      !exchangeError &&
      props.bindFormSubmission
    ) {
      props.bindFormSubmission({
        fn: () => {},
        functionality: REMOTE_BUTTON_ACCESS_FUNCTIONALITY.BANK_CREATE_PAYMENT,
      });
    }
  }, [plaidBankDetails, props.isActive, plaidState.error, exchangeError, props.bindFormSubmission]);

  const performTokenExchange = useCallback(
    async (publicToken: string, metadata: any) => {
      const account = metadata?.accounts?.[0];

      splunkReporter.contextual({
        logInfo: { logLevel: 'info', logger },
        event: 'pay',
        action: 'render',
        activityInfo: {
          activityObject: 'PlaidLink',
          plaidAction: 'starting_token_exchange',
          domainId,
        },
      });

      if (!account) {
        const noAccountError = new Error('No account returned from Plaid');
        setExchangeError(noAccountError);
        splunkReporter.contextual({
          logInfo: { logLevel: 'error', logger },
          event: 'pay',
          action: 'render',
          activityInfo: {
            activityObject: 'PlaidLink',
            plaidAction: 'no_account_returned',
            domainId,
          },
          error: { message: noAccountError.message },
        });
        return;
      }

      try {
        const currentState = store.getState();
        const { id } = await exchangePlaidToken(currentState, {
          publicToken,
        });

        if (!id) {
          throw new Error('No wallet token returned from exchange api call');
        }

        const bankDetails: PlaidBankDetails = {
          token: id,
          name: account?.name,
          accountType: account?.subtype === 'checking' ? 'PERSONAL_CHECKING' : 'PERSONAL_SAVINGS',
          phone: '0000000000',
          accountNumber: `****${account?.mask}`,
        };

        setPlaidBankDetails(bankDetails);
        setExchangeError(null);

        splunkReporter.contextual({
          logInfo: { logLevel: 'info', logger },
          event: 'pay',
          action: 'render',
          activityInfo: {
            activityObject: 'PlaidLink',
            plaidAction: 'token_exchange_success',
            accountType: bankDetails?.accountType,
            accountName: !!bankDetails?.name,
            domainId,
          },
        });
      } catch (error) {
        const originalMessage =
          error instanceof Error
            ? error.message
            : typeof error === 'string'
            ? error
            : String(error);
        setExchangeError(new Error(originalMessage));

        splunkReporter.contextual({
          logInfo: { logLevel: 'error', logger },
          event: 'pay',
          action: 'render',
          activityInfo: {
            activityObject: 'PlaidLink',
            plaidAction: 'token_exchange_error',
            domainId,
          },
          error: { message: originalMessage },
        });
      }
    },
    [store]
  );

  const handlePlaidSuccess = useCallback(
    async (publicToken: string, metadata: any) => {
      SegmentIO.trackPlaidEvent('SUCCESS', metadata, domainId, recipientEmail);
      splunkReporter.contextual({
        logInfo: { logLevel: 'info', logger },
        event: 'pay',
        action: 'render',
        activityInfo: {
          activityObject: 'PlaidLink',
          plaidAction: 'link_success',
          institutionId: metadata?.institution?.institution_id,
          accountsCount: metadata?.accounts?.length,
          domainId,
        },
      });

      await performTokenExchange(publicToken, metadata);
    },
    [performTokenExchange, domainId, recipientEmail]
  );

  const handleExchangeRetry = useCallback(() => {
    splunkReporter.contextual({
      logInfo: { logLevel: 'info', logger },
      event: 'pay',
      action: 'click',
      activityInfo: {
        activityObject: 'PlaidErrorScreen',
        plaidAction: 'restart_plaid_flow_on_exchange_error',
        domainId,
      },
    });

    SegmentIO.plaidRetryTokenExchange(domainId, recipientEmail);

    setExchangeError(null);
    setPlaidBankDetails(null);
    setPlaidState({ linkToken: null, isLoading: false, error: null });
  }, [domainId, recipientEmail]);

  const handlePlaidExit = useCallback(
    (error: any, metadata: any) => {
      SegmentIO.trackPlaidEvent('EXIT', metadata, domainId, recipientEmail);
      splunkReporter.contextual({
        logInfo: { logLevel: 'info', logger },
        event: 'pay',
        action: 'render',
        activityInfo: {
          activityObject: 'PlaidLink',
          plaidAction: 'link_exit',
          status: metadata?.status,
          errorType: error?.error_type,
          errorCode: error?.error_code,
        },
      });
    },
    [domainId, recipientEmail]
  );

  const handlePlaidEvent = useCallback(
    (eventName: string, metadata: any) => {
      splunkReporter.contextual({
        logInfo: { logLevel: 'info', logger },
        event: 'pay',
        action: 'render',
        activityInfo: {
          activityObject: 'PlaidLink',
          plaidAction: 'link_event',
          eventName,
          errorCode: metadata?.error_code,
          errorType: metadata?.error_type,
          domainId,
        },
      });

      SegmentIO.trackPlaidEvent(eventName, metadata, domainId, recipientEmail);

      if (eventName === 'ERROR') {
        splunkReporter.contextual({
          logInfo: { logLevel: 'info', logger },
          event: 'pay',
          action: 'render',
          activityInfo: {
            activityObject: 'PlaidLink',
            plaidAction: 'link_error_retry',
            errorCode: metadata?.error_code,
            retryCount: plaidLinkRetryCountRef.current,
            domainId,
          },
        });

        if (plaidLinkRetryCountRef.current < maxPlaidLinkRetries) {
          plaidLinkRetryCountRef.current += 1;
          setPlaidState({ linkToken: null, isLoading: false, error: null });
        } else {
          splunkReporter.contextual({
            logInfo: { logLevel: 'error', logger },
            event: 'pay',
            action: 'render',
            activityInfo: {
              activityObject: 'PlaidLink',
              plaidAction: 'link_retries_exhausted',
              maxRetries: maxPlaidLinkRetries,
              errorCode: metadata?.error_code,
              domainId,
            },
          });
          setPlaidState({
            linkToken: null,
            isLoading: false,
            error: new Error('Plaid link failed after maximum retries'),
          });
        }
      }
    },
    [maxPlaidLinkRetries, domainId, recipientEmail]
  );

  const handlePlaidErrorScreenOnManualEntry = useCallback(() => {
    if (typeof props.onSwitchToManual === 'function') {
      splunkReporter.contextual({
        logInfo: { logLevel: 'info', logger },
        event: 'pay',
        action: 'click',
        activityInfo: {
          activityObject: 'PlaidErrorScreen',
          plaidAction: 'error_screen_manual_entry_clicked',
          domainId,
        },
      });
      props.onSwitchToManual();
      SegmentIO.plaidErrorScreenManualEntryClicked(domainId, recipientEmail);
    }
  }, [props.onSwitchToManual, domainId, recipientEmail]);

  const handlePlaidLoad = useCallback(() => {
    // Set the plaidLoadedRef to true when the Plaid embedded link loads successfully
    plaidLoadedRef.current = true;
    splunkReporter.contextual({
      logInfo: { logLevel: 'info', logger },
      event: 'pay',
      action: 'render',
      activityInfo: {
        activityObject: 'PlaidLink',
        plaidAction: 'plaid_embedded_link_loaded_successfully',
        domainId,
      },
    });
  }, [domainId]);

  useEffect(() => {
    if (!plaidState.linkToken) return;

    plaidLoadedRef.current = false;

    const timeout = setTimeout(() => {
      if (!plaidLoadedRef.current) {
        splunkReporter.contextual({
          logInfo: { logLevel: 'error', logger },
          event: 'pay',
          action: 'render',
          activityInfo: {
            activityObject: 'PlaidEmbeddedLink',
            plaidAction: 'embedded_link_load_timeout',
            domainId: domainIdRef.current,
          },
        });

        // fallback to manual entry to reduce user friction
        onPlaidUnavailableRef.current();
      }
    }, PLAID_EMBEDDED_LINK_LOAD_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [plaidState.linkToken]);

  return (
    <Fragment>
      <div data-testid="plaid-instant-content">
        {plaidState.isLoading && (
          <div className="plaid-loading" data-testid="plaid-loading">
            <Spinner width={50} />
          </div>
        )}
        {plaidState.linkToken && (
          <div
            className="plaid-container"
            data-testid="plaid-embedded-link"
            style={{
              display: exchangeError ? 'none' : undefined,
            }}
          >
            <PlaidEmbeddedLink
              token={plaidState.linkToken}
              onSuccess={handlePlaidSuccess}
              onExit={handlePlaidExit}
              onEvent={handlePlaidEvent}
              onLoad={handlePlaidLoad}
              style={{ height: '300px' }}
            />
          </div>
        )}
        {exchangeError && (
          <PlaidErrorScreen
            onTryAgain={handleExchangeRetry}
            onEnterManually={handlePlaidErrorScreenOnManualEntry}
            domainId={domainId ?? ''}
            recipientEmail={recipientEmail ?? ''}
          />
        )}
      </div>

      <style jsx>{`
        .plaid-loading {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 48px 24px;
          min-height: 200px;
        }

        .plaid-container {
          min-height: 270px;
          border-radius: 4px;
          overflow: hidden;
        }

        @media screen and (max-width: ${breakpoints.xsl}) {
          .plaid-loading {
            padding: 32px 16px;
            min-height: 150px;
          }
        }
      `}</style>
    </Fragment>
  );
};

export default PlaidBankTabs;
